<script setup lang="ts">
/**
 * The gantt pane: one <svg>, rows drawn from the SAME `visibleRows` array the
 * tree renders, so bar n always sits beside row n.
 *
 * The axis is relative: x = 0 is now, and labels count outward (+6h, -2d, +3m).
 * See shared/axis.ts for why ticks are anchored at zero rather than the
 * viewport edge.
 */
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { freezeClock, nowMs, thawClock } from "../lib/clock.js";
import { axisTicks, hoursToMs, msToHours } from "@shared/axis.js";
import { HOUR_MS } from "@shared/types.js";
import { formatHours } from "../lib/format.js";

const board = useBoard();
const viewport = useViewport();

/**
 * Row geometry is shared with the tree pane through CSS tokens rather than
 * duplicated as numbers here: `--row-h` sizes a tree row, and `--axis-h` is the
 * blank strip the tree leaves for this axis. Read them once so the two panes
 * cannot drift apart the way they did when the axis height lived only here.
 */
const cssPx = (name: string, fallback: number): number => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};
/** Refs, re-read on resize: the phone layout makes rows taller, and a rotation
 *  can cross that breakpoint after mount. */
const ROW_H = ref(cssPx("--row-h", 30));
const HEAD_H = ref(cssPx("--axis-h", 34));

/** A fingertip cannot hit a 6px strip, so resize handles grow on touch screens. */
const coarse = window.matchMedia("(pointer: coarse)");
const handleW = ref(coarse.matches ? 16 : 6);

const wrap = ref<HTMLElement | null>(null);
const scroller = ref<HTMLElement | null>(null);
const width = ref(900);
const height = ref(600);
const dragging = ref(false);

function measure(): void {
  if (!wrap.value) return;
  width.value = wrap.value.clientWidth;
  height.value = wrap.value.clientHeight;
  ROW_H.value = cssPx("--row-h", 30);
  HEAD_H.value = cssPx("--axis-h", 34);
  handleW.value = coarse.matches ? 16 : 6;
}

let observer: ResizeObserver | null = null;
onMounted(() => {
  observer = new ResizeObserver(measure);
  if (wrap.value) observer.observe(wrap.value);
});
onBeforeUnmount(() => observer?.disconnect());

const rows = computed(() => board.visibleRows);
const contentH = computed(() => Math.max(height.value - HEAD_H.value, rows.value.length * ROW_H.value));

const h1 = computed(() => viewport.h0 + width.value / viewport.pxPerHour);
const ticks = computed(() =>
  axisTicks({
    h0: viewport.h0,
    h1: h1.value,
    pxPerHour: viewport.pxPerHour,
    nowMs: nowMs.value,
  }),
);

const x = (ms: number): number => (msToHours(ms, nowMs.value) - viewport.h0) * viewport.pxPerHour;

/** Geometry guard. A NaN width or x is not an error in SVG — the attribute is
 *  just ignored and the shape lands at 0 — so nothing bad may reach an attr. */
const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);
const nowX = computed(() => (0 - viewport.h0) * viewport.pxPerHour);

/**
 * Bars for rows that have an effective span (own dates, or their children's),
 * plus PROVISIONAL bars for unscheduled rows that carry an estimate: those run
 * from now for `estHours`, so estimated work is visible on the chart instead of
 * drawing nothing at all. They are drawn hollow and are not draggable — there
 * are no dates to move, and a drag would have to invent a start the user never
 * chose. Set the dates in the drawer and the bar becomes real.
 *
 * A provisional bar is anchored by its PROGRESS, not by its left edge: it is
 * pulled back so the finished fraction sits behind the now-line and the
 * remaining work starts at it. Half of a 10h task done means 5h behind now and
 * 5h ahead, which is the honest reading of "5 hours left". Dated bars are not
 * moved — their edges are facts the user typed, and sliding them would make the
 * chart disagree with the drawer — so there progress stays a fill.
 *
 * Once a row is FINISHED, its provisional bar is drawn from the time actually
 * spent rather than from the estimate: an estimate stops being the interesting
 * number the moment there is a real one to replace it with, and a finished row
 * sits entirely behind the now-line, so its length reads as "this is what it
 * took". A finished row with no logged time keeps its estimate — there is
 * nothing better to draw — and one with logged time but no estimate now gets a
 * bar where before it got none.
 */
interface Bar {
  id: string;
  y: number;
  x: number;
  w: number;
  isParent: boolean;
  percent: number;
  color: string;
  title: string;
  label: string;
  overflows: boolean;
  provisional: boolean;
  /** The task's own status is done: drawn green, whoever it was assigned to. */
  isDone: boolean;
  /** Tooltip: what the width means, in words. */
  hint: string;
}

const bars = computed((): Bar[] => {
  const out: Bar[] = [];
  rows.value.forEach((row, i) => {
    if (row.kind === "add") return; // a blank line opposite the tree's button
    const { startMs, endMs, estHours, spentHours } = row.rollup;
    const scheduled = startMs !== null && endMs !== null;
    const isTask = row.kind === "task";
    const done = clamp01(row.rollup.percent);
    const finished = done >= 1;

    // Finished and timed: draw what it took. Otherwise draw what it is expected
    // to take. Either way it is a count of hours turned into a width.
    const spent = Number.isFinite(spentHours) ? spentHours : 0;
    const useSpent = finished && spent > 0;
    const hours = useSpent ? spent : estHours;
    if (!scheduled && !(hours && hours > 0)) return;

    const w = scheduled ? x(endMs) - x(startMs) : hours! * viewport.pxPerHour;
    // Unscheduled: hang the bar back by the part already done, so the boundary
    // between done and remaining lands exactly on the now-line. A finished bar
    // therefore ends on it.
    const x0 = scheduled ? x(startMs) : nowX.value - w * done;

    const isDone = isTask && row.task.status === "done";
    const title = isTask ? row.task.title : row.label;
    const measure = useSpent ? `${formatHours(spent)} spent` : `~${formatHours(hours!)} estimated`;
    out.push({
      id: row.key,
      // Centred in the row, whatever height the layout gives it.
      y: i * ROW_H.value + (ROW_H.value - 16) / 2,
      // A heading spans everything under it, so it draws like a parent.
      isParent: isTask ? row.hasChildren : true,
      x: x0,
      w: Math.max(3, w),
      percent: done,
      color: isDone
        ? "var(--st-done)"
        : isTask
        ? (row.task.assignees.length ? board.userColor(row.task.assignees[0]!) : "var(--st-doing)")
        : (row.color ?? "var(--st-doing)"),
      title,
      label: scheduled ? title : `${title} · ${useSpent ? formatHours(spent) : `~${formatHours(hours!)}`}`,
      hint: scheduled ? title : `${title} — unscheduled, ${measure}`,
      overflows: scheduled && row.rollup.overflows,
      provisional: !scheduled,
      isDone,
    });
  });
  return out;
});

const barById = computed(() => new Map(bars.value.map((b) => [b.id, b])));

/** Dependency arrows: elbow out of the blocker's end into the blocked task's start. */
const arrows = computed(() => {
  const out: { d: string; key: string }[] = [];
  for (const row of rows.value) {
    if (row.kind !== "task") continue; // a heading blocks nothing
    const to = barById.value.get(row.task.id);
    if (!to) continue;
    for (const blockerId of row.task.blockedBy) {
      const from = barById.value.get(blockerId);
      if (!from) continue;
      const x0 = from.x + from.w;
      const y0 = from.y + 8;
      const x1 = to.x;
      const y1 = to.y + 8;
      const mid = x1 > x0 + 14 ? x0 + 10 : x0 + 10;
      out.push({
        key: `${blockerId}->${row.task.id}`,
        d: `M ${x0} ${y0} H ${mid} V ${y1} H ${x1}`,
      });
    }
  }
  return out;
});

// ── interaction ──────────────────────────────────────────────────────────
function onWheel(ev: WheelEvent): void {
  if (!ev.ctrlKey && !ev.metaKey && Math.abs(ev.deltaY) > Math.abs(ev.deltaX)) return; // let it scroll
  ev.preventDefault();
  const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
  viewport.zoom(ev.deltaY < 0 ? 1.15 : 1 / 1.15, ev.clientX - rect.left);
}

/**
 * Pan with one pointer, pinch-zoom with two. The svg is `touch-action: pan-y`,
 * so the browser keeps vertical scrolling and hands horizontal drags and pinches
 * to us. Every pointer currently down is tracked; the gesture is re-based
 * whenever one lands or lifts, so going 1 → 2 → 1 fingers never jumps.
 */
const pointers = new Map<number, number>();
let lastX = 0;
let lastSpread = 0;

function gesture(): { x: number; spread: number } {
  const xs = [...pointers.values()];
  if (xs.length < 2) return { x: xs[0] ?? 0, spread: 0 };
  return { x: (xs[0]! + xs[1]!) / 2, spread: Math.abs(xs[0]! - xs[1]!) };
}

function onPanStart(ev: PointerEvent): void {
  if (ev.pointerType === "mouse" && ev.button !== 0) return;
  const left = (ev.currentTarget as Element).getBoundingClientRect().left;
  pointers.set(ev.pointerId, ev.clientX);
  ({ x: lastX, spread: lastSpread } = gesture());
  if (pointers.size > 1) return; // listeners are already attached

  dragging.value = true;
  freezeClock();
  const move = (e: PointerEvent): void => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, e.clientX);
    const g = gesture();
    if (pointers.size > 1 && lastSpread > 0 && g.spread > 0) {
      viewport.zoom(g.spread / lastSpread, g.x - left);
    }
    viewport.pan(g.x - lastX);
    lastX = g.x;
    lastSpread = g.spread;
  };
  const up = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    ({ x: lastX, spread: lastSpread } = gesture());
    if (pointers.size > 0) return;
    dragging.value = false;
    thawClock();
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", up);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
}

/** Drag a bar to reschedule. Snaps to whole hours — this is where "hour
 *  granularity" stops being a claim and becomes physically true. */
function onBarDown(bar: Bar, ev: PointerEvent, mode: "move" | "start" | "end"): void {
  // Nothing to reschedule: a provisional bar has no dates, and a group heading
  // has no task at all. Checked BEFORE stopPropagation so the drag falls through
  // to the pan handler instead of dying on a bar that cannot move.
  const task = board.tasks.get(bar.id);
  if (bar.provisional || !task || task.startMs === null || task.endMs === null) return;
  ev.stopPropagation();

  freezeClock();
  const originX = ev.clientX;
  const startMs = task.startMs;
  const endMs = task.endMs;
  let deltaHours = 0;
  let next = { startMs, endMs };

  // A preview, not a write to the store: anyone else's change to the task
  // mid-drag (a rename, their own reschedule) lands under the bar, not over it.
  const move = (e: PointerEvent): void => {
    deltaHours = Math.round((e.clientX - originX) / viewport.pxPerHour);
    next =
      mode === "move"
        ? { startMs: startMs + deltaHours * HOUR_MS, endMs: endMs + deltaHours * HOUR_MS }
        : mode === "start"
          ? { startMs: Math.min(startMs + deltaHours * HOUR_MS, endMs - HOUR_MS), endMs }
          : { startMs, endMs: Math.max(endMs + deltaHours * HOUR_MS, startMs + HOUR_MS) };
    board.previewTask(bar.id, next);
  };

  const up = (): void => {
    thawClock();
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", up);
    board.endPreview(bar.id); // the optimistic patch owns the change from here
    if (deltaHours === 0 || !board.tasks.has(bar.id)) return;
    void board.patchTask(bar.id, next);
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
}

// Vertical scroll sync with the tree.
let syncing = false;
function onScroll(): void {
  if (syncing || !scroller.value) return;
  syncing = true;
  viewport.scrollTop = scroller.value.scrollTop;
  requestAnimationFrame(() => (syncing = false));
}
watch(
  () => viewport.scrollTop,
  (top) => {
    if (syncing || !scroller.value) return;
    syncing = true;
    scroller.value.scrollTop = top;
    requestAnimationFrame(() => (syncing = false));
  },
);
// The panes take turns between views; one coming back into view picks up where
// the other left off instead of wherever it was when it was hidden.
watch(
  () => viewport.view,
  async () => {
    await nextTick();
    measure();
    if (scroller.value) scroller.value.scrollTop = viewport.scrollTop;
  },
);
</script>

<template>
  <div class="pane-head">
    <span>Timeline</span>
    <span class="wide-only" style="color: var(--faint); text-transform: none; letter-spacing: 0">
      · relative to now · {{ ticks.step.n }}{{ ticks.step.unit }} per gridline
    </span>
    <span style="flex: 1"></span>
    <button class="btn ghost" @click="viewport.jumpToNow(width)">now</button>
    <button class="btn ghost" @click="viewport.setZoom(viewport.pxPerHour / 1.6)">−</button>
    <button class="btn ghost" @click="viewport.setZoom(viewport.pxPerHour * 1.6)">+</button>
  </div>

  <div ref="wrap" class="gantt-wrap">
    <!-- axis: fixed, does not scroll vertically -->
    <svg class="gantt-svg" :width="width" :height="HEAD_H" @wheel="onWheel" @pointerdown="onPanStart">
      <g v-for="t in ticks.major" :key="'a' + t.hours">
        <line class="grid-line" :x1="t.x" :x2="t.x" y1="20" :y2="HEAD_H" />
        <text class="axis-label" :class="{ zero: t.hours === 0 }" :x="t.x + 4" y="14">{{ t.label }}</text>
      </g>
    </svg>

    <div
      ref="scroller"
      class="scroll"
      :style="{ height: `calc(100% - ${HEAD_H}px)` }"
      @scroll="onScroll"
    >
      <svg
        class="gantt-svg"
        :class="{ dragging }"
        :width="width"
        :height="contentH"
        @wheel="onWheel"
        @pointerdown="onPanStart"
      >
        <defs>
          <pattern id="hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--st-blocked)" stroke-width="2" opacity=".55" />
          </pattern>
          <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 z" fill="var(--faint)" />
          </marker>
        </defs>

        <line
          v-for="t in ticks.minor"
          :key="'n' + t.hours"
          class="grid-line minor"
          :x1="t.x" :x2="t.x" y1="0" :y2="contentH"
        />
        <line
          v-for="t in ticks.major"
          :key="'g' + t.hours"
          class="grid-line"
          :x1="t.x" :x2="t.x" y1="0" :y2="contentH"
        />

        <line class="now-line" :x1="nowX" :x2="nowX" y1="0" :y2="contentH" />

        <path
          v-for="a in arrows"
          :key="a.key"
          class="dep-arrow"
          :d="a.d"
          marker-end="url(#arrow)"
        />

        <g v-for="bar in bars" :key="bar.id" @pointerdown="onBarDown(bar, $event, 'move')">
          <title>{{ bar.hint }}</title>
          <rect
            class="bar"
            :class="{ 'bar-parent': bar.isParent && !bar.provisional, 'bar-provisional': bar.provisional, 'bar-done': bar.isDone }"
            :x="bar.x" :y="bar.y" :width="bar.w" :height="16"
            :fill="bar.provisional ? 'none' : bar.isParent ? undefined : bar.color"
            :stroke="bar.provisional ? bar.color : undefined"
            :opacity="bar.provisional || bar.isParent ? undefined : 0.35"
          />
          <rect
            v-if="!bar.isParent"
            class="bar"
            :x="bar.x" :y="bar.y" :width="Math.max(0, bar.w * bar.percent)" :height="16"
            :fill="bar.color"
            :opacity="bar.provisional ? 0.45 : undefined"
          />
          <rect
            v-if="bar.overflows"
            class="bar overflow-hatch"
            :x="bar.x" :y="bar.y" :width="bar.w" :height="16"
          />
          <template v-if="!bar.isParent && !bar.provisional">
            <rect
              :x="bar.x - handleW / 2" :y="bar.y" :width="handleW" :height="16" fill="transparent"
              style="cursor: ew-resize"
              @pointerdown.stop="onBarDown(bar, $event, 'start')"
            />
            <rect
              :x="bar.x + bar.w - handleW / 2" :y="bar.y" :width="handleW" :height="16" fill="transparent"
              style="cursor: ew-resize"
              @pointerdown.stop="onBarDown(bar, $event, 'end')"
            />
          </template>
          <text
            class="bar-label"
            :class="{ 'bar-label-provisional': bar.provisional }"
            :x="bar.x + bar.w + 6"
            :y="bar.y + 12"
          >{{ bar.label }}</text>
        </g>
      </svg>
    </div>
  </div>
</template>
